
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, MessageSquare, TrendingUp, ChartBar, Award, Target, Star, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Stat {
  id: string;
  value: string;
  label_key: string;
  icon_name: string;
  display_order: number;
}

const StatsSection = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<Stat[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      const { data } = await supabase
        .from('stats')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (data) setStats(data as Stat[]);
    };

    fetchStats();
  }, []);

  const getIcon = (iconName: string) => {
    const iconMap: { [key: string]: JSX.Element } = {
      Users: <Users className="h-8 w-8 text-brand-yellow" />,
      MessageSquare: <MessageSquare className="h-8 w-8 text-brand-yellow" />,
      ChartBar: <ChartBar className="h-8 w-8 text-brand-yellow" />,
      TrendingUp: <TrendingUp className="h-8 w-8 text-brand-yellow" />,
      Award: <Award className="h-8 w-8 text-brand-yellow" />,
      Target: <Target className="h-8 w-8 text-brand-yellow" />,
      Star: <Star className="h-8 w-8 text-brand-yellow" />,
      CheckCircle: <CheckCircle className="h-8 w-8 text-brand-yellow" />,
    };
    return iconMap[iconName] || <Users className="h-8 w-8 text-brand-yellow" />;
  };

  if (stats.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("stats.title")}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t("stats.subtitle")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div 
              key={stat.id} 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow"
            >
              <div className="flex justify-center mb-4">{getIcon(stat.icon_name)}</div>
              <div className="text-3xl md:text-4xl font-bold mb-2">{stat.value}</div>
              <div className="text-muted-foreground">{t(stat.label_key)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;